import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Clock, X, CreditCard } from 'lucide-react'
import { subscriptionService, Subscription } from '../../services/subscriptionService'
import { useAuthStore } from '../../stores/authStore'
import { Button } from '../ui'
import { ROUTES } from '@/router/routes'

// Helper function to calculate days remaining
const calculateDaysRemaining = (endDate: string | null): number => {
  if (!endDate) return 0
  const end = new Date(endDate)
  const now = new Date()
  const diffTime = end.getTime() - now.getTime()
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))
  return Math.max(0, diffDays)
}

// Format date for display
const formatDate = (dateString: string | null): string => {
  if (!dateString) return ''
  try {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric'
    })
  } catch {
    return ''
  }
}

const TrialBanner: React.FC = () => {
  const { user } = useAuthStore()
  const navigate = useNavigate()
  const [subscription, setSubscription] = useState<Subscription | null>(null)
  const [dismissed, setDismissed] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchSubscription = async () => {
      try {
        const response = await subscriptionService.getCurrentSubscription()
        if (response.success && response.data?.subscription) {
          setSubscription(response.data.subscription)
        }
      } catch (error) {
        console.error('Error fetching subscription:', error)
      } finally {
        setLoading(false)
      }
    }

    if (user?.organizationId) {
      fetchSubscription()
    } else {
      setLoading(false)
    }
  }, [user?.organizationId])

  // Don't show if loading, dismissed, or not trialing
  if (loading || dismissed) return null
  if (!subscription || subscription.status !== 'trialing') return null

  const daysRemaining = calculateDaysRemaining(subscription.trial_end)

  // Don't show if trial has ended
  if (daysRemaining <= 0) return null

  // Determine urgency level
  const isUrgent = daysRemaining <= 3
  const isWarning = daysRemaining <= 7

  return (
    <div className={`w-full px-4 py-2 flex items-center justify-center gap-3 text-sm ${
      isUrgent
        ? 'bg-orange-500 text-white'
        : isWarning
        ? 'bg-yellow-400 text-yellow-900'
        : 'bg-blue-500 text-white'
    }`}>
      <Clock className='h-4 w-4 flex-shrink-0' />
      <span className='font-medium'>
        {isUrgent ? (
          <>Trial ends in {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'}!</>
        ) : (
          <>Free trial: {daysRemaining} {daysRemaining === 1 ? 'day' : 'days'} remaining (ends {formatDate(subscription.trial_end)})</>
        )}
      </span>

      {isWarning && (
        <Button
          variant='outline'
          size='sm'
          onClick={() => navigate(ROUTES.SETTINGS)}
          className={`ml-2 text-xs py-1 px-2 h-auto ${
            isUrgent
              ? 'border-white text-white hover:bg-orange-600'
              : isWarning
              ? 'border-yellow-700 text-yellow-900 hover:bg-yellow-500'
              : 'border-white text-white hover:bg-blue-600'
          }`}
        >
          <CreditCard className='h-3 w-3 mr-1' />
          Add Payment
        </Button>
      )}

      <button
        onClick={() => setDismissed(true)}
        className={`ml-auto p-1 rounded hover:bg-black/10 transition-colors ${
          isUrgent || !isWarning ? 'text-white/80 hover:text-white' : 'text-yellow-800/80 hover:text-yellow-900'
        }`}
        aria-label='Dismiss trial banner'
      >
        <X className='h-4 w-4' />
      </button>
    </div>
  )
}

export default TrialBanner
